import type { GuardrailActionKind } from '@matter_extractor/shared';

/**
 * The words Réglages > Règles speaks about its two lists (§8.5, §11.3).
 *
 * Same régime as `persona-labels.ts` and `versioned-labels.ts`: a refusal is
 * worded from its CODE and never from the server's sentence, which is a
 * developer string bound for the logs and the OpenAPI. Two spellings of one
 * refusal drift, and the half that drifts is the one nobody reads.
 *
 * Its own module rather than a branch of `persona-labels.ts`, because these two
 * lists are not those two: they have their own catalogue - one code where §11.5
 * has two - and folding them together would offer three codes where one exists.
 */

/* ------------------------------------------------- the words of a guardrail */

/** The five actions of §8.5, as the select names them. */
export const GUARDRAIL_ACTION_LABEL: Readonly<Record<GuardrailActionKind, string>> = {
  'close-experience': 'Fermer l’expérience',
  'close-protocol': 'Clôturer le protocole',
  'conclude-with-farewell': 'Conclure par l’adieu',
  'inject-direction': 'Injecter une direction à l’interviewer',
  'flag-only': 'Signaler seulement',
};

/**
 * What each action DOES, under the select - one line, in the present tense.
 *
 * They are on the screen rather than in the spec alone because the four are not
 * symmetric: two end something, one changes the next turn, one changes nothing
 * an interviewer can see. « Clôturer le protocole » and « fermer l'expérience »
 * read as synonyms until somebody says that the first lets the timeline carry
 * on (§8.4).
 */
export const GUARDRAIL_ACTION_HINT: Readonly<Record<GuardrailActionKind, string>> = {
  'close-experience':
    'La session s’arrête, avec le texte de clôture par défaut. Aucune page suivante.',
  'close-protocol': 'La conversation s’arrête, la timeline de l’expérience continue.',
  'conclude-with-farewell':
    'Va à l’étape d’adieu du protocole joué ; sans étape d’adieu, clôt le protocole. La personne continue à la page suivante.',
  'inject-direction': 'L’interviewer reçoit cette direction au tour suivant. Rien ne s’arrête.',
  'flag-only': 'Rien ne change pour la personne. L’incident est visible dans Sessions.',
};

/**
 * « pendant N tours consécutifs », said in full under the field.
 *
 * Written out rather than glued from a count and a verb, the lesson
 * `levelDeletionSentence` records: French agreement is not a plural `s`, and
 * « 1 tours consécutifs » is what a ternary that only knew about zero produces.
 */
export function consecutiveTurnsSentence(turns: number): string {
  if (!Number.isFinite(turns) || turns < 1)
    return 'Le moteur tient la série des tours consécutifs.';
  return turns === 1
    ? 'Se déclenche dès qu’un tour est détecté. Un tour non détecté remet la série à zéro.'
    : `Se déclenche après ${turns} tours détectés d’affilée. Un tour non détecté remet la série à zéro.`;
}
